import * as React from "react";

const readStoredColumnList = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch {
    return [];
  }
};

/**
 * Hidden columns and column/note-column order, persisted per collection in localStorage.
 * The collection route switches collections without remounting the grid, so the lists are
 * re-read during render when the collection changes; otherwise the previous collection's
 * layout would leak into the next one.
 */
export const useSnapshotGridColumnLayout = (collectionId: string | undefined) => {
  const hiddenColumnsKey = `snapshot-hidden-columns-${collectionId}`;
  const columnOrderKey = `snapshot-column-order-${collectionId}`;
  const noteColumnOrderKey = `snapshot-note-column-order-${collectionId}`;

  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(() => readStoredColumnList(hiddenColumnsKey));
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => readStoredColumnList(columnOrderKey));
  const [noteColumnOrder, setNoteColumnOrder] = React.useState<string[]>(() =>
    readStoredColumnList(noteColumnOrderKey),
  );
  const [loadedCollectionId, setLoadedCollectionId] = React.useState(collectionId);

  if (loadedCollectionId !== collectionId) {
    setLoadedCollectionId(collectionId);
    setHiddenColumns(readStoredColumnList(hiddenColumnsKey));
    setColumnOrder(readStoredColumnList(columnOrderKey));
    setNoteColumnOrder(readStoredColumnList(noteColumnOrderKey));
  }

  const handleHiddenColumnsChange = (columns: string[]) => {
    setHiddenColumns(columns);
    localStorage.setItem(hiddenColumnsKey, JSON.stringify(columns));
  };

  const handleColumnOrderChange = (newOrder: string[]) => {
    setColumnOrder(newOrder);
    localStorage.setItem(columnOrderKey, JSON.stringify(newOrder));
  };

  const handleNoteColumnOrderChange = (newOrder: string[]) => {
    setNoteColumnOrder(newOrder);
    localStorage.setItem(noteColumnOrderKey, JSON.stringify(newOrder));
  };

  const removeNoteColumn = React.useCallback(
    (columnName: string) => {
      setHiddenColumns(prev => {
        if (!prev.includes(columnName)) return prev;
        const next = prev.filter(c => c !== columnName);
        localStorage.setItem(hiddenColumnsKey, JSON.stringify(next));
        return next;
      });
      setNoteColumnOrder(prev => {
        if (!prev.includes(columnName)) return prev;
        const next = prev.filter(c => c !== columnName);
        localStorage.setItem(noteColumnOrderKey, JSON.stringify(next));
        return next;
      });
    },
    [hiddenColumnsKey, noteColumnOrderKey],
  );

  return {
    hiddenColumns,
    setHiddenColumns,
    handleHiddenColumnsChange,
    columnOrder,
    setColumnOrder,
    handleColumnOrderChange,
    noteColumnOrder,
    setNoteColumnOrder,
    handleNoteColumnOrderChange,
    removeNoteColumn,
  };
};
